import React, { useState } from "react";

import { css, cx } from "@emotion/css";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useSearchPatientsList } from "@/hooks/usePatientList.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { BackToTop } from "@/uiKit/molecules/BackToTop.tsx";
import { PatientCreation } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreation.tsx";
import { PatientListMenu } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";
import { PatientListSearch } from "@/uiKit/organisms/PatientsList/PatientListSearch.tsx";
import { PatientListTable } from "@/uiKit/organisms/PatientsList/PatientListTable.tsx";

export type DoctorPatientListCardProps = {
  className?: string;
};

export const DoctorPatientListCard: React.FC<DoctorPatientListCardProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [openCreatePatient, setOpenCreatePatient] = useState<boolean>(false);
  const searchParams = useSearch({
    from: "/_practitioner/patients/",
  });
  const urlSelectedTab = searchParams.menu ?? "monitored";
  const data = Queries.practitioner.useDoctorPatientList({});
  const patientsListData = useSearchPatientsList(data);
  const navigate = useNavigate();

  return (
    <Card id="doctor-patients-list-card" className={cx(styles.container, className)}>
      <CardHeader
        className={styles.header}
        variant="plain"
        title={
          <>
            <PatientListMenu className={styles.menu} selectedItem={urlSelectedTab} patientListData={patientsListData} />
            <PatientListSearch />
          </>
        }
        action={
          <Button
            id="create-patient-button"
            className={styles.createPatient}
            variant="contained"
            data-action="patients.patient.create"
            startIcon={<Icons.userPlus className={styles.icon} />}
            onClick={() => setOpenCreatePatient(true)}
          >
            {t("pages.patients.addPatient")}
          </Button>
        }
      />
      <CardContent className={styles.content}>
        <PatientListTable
          id="doctor-patient-list-table"
          qualification="doctor"
          tab={urlSelectedTab}
          data={patientsListData[urlSelectedTab]}
        />
        <BackToTop id="back-to-top-of-patient-list" threshold={100} />
        <PatientCreation
          id="patient-creation-from-patient-list"
          open={openCreatePatient}
          onClose={() => setOpenCreatePatient(false)}
          onSubmit={(patientId: string) => {
            setOpenCreatePatient(false);
            TextToast.success(t("diabetesForms.patientCreated"), t("diabetesForms.clickToView"), {
              onClick: () =>
                navigate({
                  to: `/patients/${patientId}`,
                }),
            });
          }}
        />
      </CardContent>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
  }),
  header: css({
    flexWrap: "wrap",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    position: "sticky",
    top: 0,
    zIndex: 3,
  }),
  menu: css({}),
  loader: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  createPatient: css({
    alignSelf: "center",
    textWrap: "nowrap",
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: "1 1",
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
